/**
 * Offline-Ausführung der Änderungen, die während einer Session zählen: Notiztext,
 * Eigenschaften, Session-Log, Karten (Tokens, Pins, Nebel, Schablonen) und der Kampf-Tracker.
 * Jede Funktion liest den lokalen Stand, wendet dieselben Regeln wie der Server an
 * (gemeinsame Funktionen aus `shared`) und liefert die Antwort, die der Server geben würde.
 * Die Änderung selbst wird gepuffert und später an den Server gesendet.
 */
import {
  appendLogSchema,
  cleanFrontmatter,
  CombatRuleError,
  createResolver,
  deadLockViolation,
  extractLinks,
  hpChangeSchema,
  insertLogLine,
  linkKey,
  normalizeGrid,
  normalizeTemplates,
  patchFrontmatter,
  patchFrontmatterSchema,
  planCombatantUpdate,
  planHpChange,
  planTurn,
  sortCombatants,
  turnSchema,
  updateCombatantSchema,
  updateMapSchema,
  updateNoteSchema,
  updatePinSchema,
  updateTokenSchema,
  appendCombatLog,
  type CampaignDTO,
  type CombatActionResult,
  type CombatantDTO,
  type CombatDTO,
  type CombatListEntry,
  type FileEntry,
  type MapDTO,
  type NoteDetail,
  type NoteIndexEntry,
  type NoteWithProps,
  type ResolvedLinkTarget,
} from '@kartothek/shared';
import { ApiError } from '../apiError';
import { t } from '../i18n';
import { cachedUrls, campaignPrefix, combatUrl, mapUrl, noteUrl, readCached, writeCached } from './cache';

type Handler = (p: string[], body: unknown) => Promise<unknown>;

interface Route {
  method: string;
  re: RegExp;
  run: Handler;
}

const ID = '([0-9a-f-]{36})';
const C = `^/api/campaigns/${ID}`;

/** Zod-Schema aus `shared` (strukturell, damit der Web-Client kein eigenes zod braucht). */
interface Schema<T> {
  safeParse(x: unknown): { success: true; data: T } | { success: false; error: { issues: { path: (string | number)[]; message: string }[] } };
}

/** Eingaben wie der Server prüfen – gleiche Fehlermeldungen wie online. */
function parse<T>(schema: Schema<T>, body: unknown): T {
  const r = schema.safeParse(body ?? {});
  if (r.success) return r.data;
  throw new ApiError(400, 'validation', r.error.issues.map((i) => `${i.path.join('.') || 'Eingabe'}: ${i.message}`).join('; '));
}

/** Nur was lokal vorliegt, lässt sich offline ändern. */
async function need<T>(url: string): Promise<T> {
  const data = await readCached<T>(url);
  if (data === undefined) throw new ApiError(0, 'offline', t('offline.notCached'));
  return data;
}

function rule<T>(fn: () => T): T {
  try {
    return fn();
  } catch (e) {
    if (e instanceof CombatRuleError) throw new ApiError(400, e.code, e.message);
    throw e;
  }
}

// ---------- Notizen ----------

/** Neue Linkziele im Text lokal auflösen (gegen Notiz-Index und Dateiliste der lokalen Kopie). */
async function resolveNewLinks(cid: string, note: NoteDetail): Promise<Record<string, ResolvedLinkTarget>> {
  const found = extractLinks(note.body, note.frontmatter);
  const missing = found.filter((l) => !note.links[linkKey(l.rawTarget)]);
  if (missing.length === 0) return note.links;
  const index = (await readCached<NoteIndexEntry[]>(`${campaignPrefix(cid)}/notes`)) ?? [];
  const files = (await readCached<FileEntry[]>(`${campaignPrefix(cid)}/files`)) ?? [];
  const resolver = createResolver(
    index.map((n) => ({ id: n.id, folder: n.folder, title: n.title })),
    files.map((f) => ({ id: f.id, folder: f.folder, name: f.name })),
  );
  const byId = new Map(index.map((n) => [n.id, n]));
  const fileById = new Map(files.map((f) => [f.id, f]));
  const links = { ...note.links };
  for (const l of missing) {
    const r = resolver.resolve(l.rawTarget, note.folder);
    if (r?.type === 'note' && byId.has(r.id)) {
      const n = byId.get(r.id)!;
      links[linkKey(l.rawTarget)] = { type: 'note', id: n.id, title: n.title, folder: n.folder, typeKey: n.typeKey };
    } else if (r?.type === 'file' && fileById.has(r.id)) {
      const f = fileById.get(r.id)!;
      links[linkKey(l.rawTarget)] = { type: 'file', id: f.id, name: f.name, mime: f.mime, url: f.url };
    }
  }
  return links;
}

/** Listen mit Eigenschaften (Quests, Sessions, Charaktere) mitziehen. */
async function patchNoteLists(cid: string, note: NoteDetail) {
  for (const url of await cachedUrls(`${campaignPrefix(cid)}/types/`)) {
    if (!url.endsWith('/notes')) continue;
    const list = await readCached<NoteWithProps[]>(url);
    if (!list?.some((n) => n.id === note.id)) continue;
    await writeCached(
      url,
      list.map((n) => (n.id === note.id ? { ...n, frontmatter: note.frontmatter, visibility: note.visibility } : n)),
    );
  }
}

async function saveNote(cid: string, before: NoteDetail, patch: Partial<NoteDetail>): Promise<NoteDetail> {
  if (patch.frontmatter && deadLockViolation(before.frontmatter, patch.frontmatter)) {
    throw new ApiError(409, 'dead', 'Verstorben: Spielwerte sind gesperrt, nur das Inventar lässt sich ändern.');
  }
  // `updatedAt` bleibt: Weitere Offline-Änderungen bauen auf demselben Server-Stand auf
  const next: NoteDetail = { ...before, ...patch };
  next.links = await resolveNewLinks(cid, next);
  await writeCached(noteUrl(cid, next.id), next);
  await patchNoteLists(cid, next);
  return next;
}

/** Wie der Server: Spieler bearbeiten nur eigene Notizen. */
function assertCanEdit(note: NoteDetail) {
  if (note.canEdit === false) throw new ApiError(403, 'forbidden', 'Nur eigene Notizen lassen sich bearbeiten');
}

const updateNote: Handler = async ([cid, nid], body) => {
  const input = parse(updateNoteSchema, body);
  const note = await need<NoteDetail>(noteUrl(cid!, nid!));
  assertCanEdit(note);
  const renamed =
    (input.title !== undefined && input.title !== note.title) ||
    (input.folder !== undefined && input.folder !== note.folder) ||
    (input.typeKey !== undefined && input.typeKey !== note.typeKey);
  // Umbenennen schreibt Links in anderen Notizen um – das kann nur der Server
  if (renamed) throw new ApiError(0, 'offline', t('offline.renameOffline'));
  if (input.baseUpdatedAt && new Date(input.baseUpdatedAt).getTime() !== new Date(note.updatedAt).getTime()) {
    throw new ApiError(409, 'stale', 'Die Notiz wurde inzwischen geändert. Bitte neu laden.');
  }
  return saveNote(cid!, note, {
    body: input.body ?? note.body,
    frontmatter: input.frontmatter ? cleanFrontmatter(input.frontmatter) : note.frontmatter,
    visibility: input.visibility ?? note.visibility,
    visiblePlayerIds: input.visiblePlayerIds === undefined ? note.visiblePlayerIds : input.visiblePlayerIds,
    ownerId: input.ownerId === undefined ? note.ownerId : input.ownerId,
  });
};

const patchNoteFrontmatter: Handler = async ([cid, nid], body) => {
  const { set, unset } = parse(patchFrontmatterSchema, body);
  const note = await need<NoteDetail>(noteUrl(cid!, nid!));
  assertCanEdit(note);
  return saveNote(cid!, note, { frontmatter: cleanFrontmatter(patchFrontmatter(note.frontmatter, set, unset)) });
};

const appendLog: Handler = async ([cid, nid], body) => {
  const { text, time } = parse(appendLogSchema, body);
  const note = await need<NoteDetail>(noteUrl(cid!, nid!));
  return saveNote(cid!, note, { body: insertLogLine(note.body, time ? `${time} ${text}` : text) });
};

// ---------- Karten ----------

async function saveMap(cid: string, map: MapDTO): Promise<MapDTO> {
  await writeCached(mapUrl(cid, map.noteId), map);
  return map;
}

const updateMap: Handler = async ([cid, nid], body) => {
  const input = parse(updateMapSchema, body);
  const map = await need<MapDTO>(mapUrl(cid!, nid!));
  return saveMap(cid!, {
    ...map,
    ...(input.grid ? { grid: normalizeGrid({ ...map.grid, ...input.grid }) } : {}),
    ...(input.fog ? { fog: input.fog } : {}),
    ...(input.templates ? { templates: normalizeTemplates(input.templates) } : {}),
    ...(input.image ? { image: input.image } : {}),
    updatedAt: new Date().toISOString(),
  });
};

const updatePin: Handler = async ([cid, nid, pid], body) => {
  const input = parse(updatePinSchema, body);
  const map = await need<MapDTO>(mapUrl(cid!, nid!));
  if (!map.pins.some((p) => p.id === pid)) throw new ApiError(404, 'not_found', 'Pin nicht gefunden');
  if (input.noteId !== undefined && input.noteId !== map.pins.find((p) => p.id === pid)!.noteId) {
    throw new ApiError(0, 'offline', t('offline.unsupported'));
  }
  return saveMap(cid!, { ...map, pins: map.pins.map((p) => (p.id === pid ? { ...p, ...input, noteId: p.noteId } : p)) });
};

const updateToken: Handler = async ([cid, nid, tid], body) => {
  const input = parse(updateTokenSchema, body);
  const map = await need<MapDTO>(mapUrl(cid!, nid!));
  const token = map.tokens.find((x) => x.id === tid);
  if (!token) throw new ApiError(404, 'not_found', 'Token nicht gefunden');
  // Verknüpfung und Bild brauchen die Auflösung des Servers
  if ((input.noteId !== undefined && input.noteId !== token.noteId) || (input.image !== undefined && input.image !== token.image)) {
    throw new ApiError(0, 'offline', t('offline.unsupported'));
  }
  return saveMap(cid!, { ...map, tokens: map.tokens.map((x) => (x.id === tid ? { ...x, ...input, noteId: x.noteId, image: x.image } : x)) });
};

// ---------- Kampf ----------

async function rulesetOf(cid: string): Promise<'2014' | '2024'> {
  const c = await readCached<CampaignDTO>(campaignPrefix(cid));
  return c?.ruleset === '5e-2024' ? '2024' : '2014';
}

async function saveCombat(cid: string, combat: CombatDTO): Promise<CombatDTO> {
  const next = { ...combat, combatants: sortCombatants(combat.combatants), updatedAt: new Date().toISOString() };
  await writeCached(combatUrl(cid, next.id), next);
  const listUrl = `${campaignPrefix(cid)}/combats`;
  const list = await readCached<CombatListEntry[]>(listUrl);
  if (list) {
    await writeCached(
      listUrl,
      list.map((e) => (e.id === next.id ? { ...e, status: next.status, round: next.round, updatedAt: next.updatedAt } : e)),
    );
  }
  return next;
}

function combatantOf(combat: CombatDTO, bid: string): CombatantDTO {
  const r = combat.combatants.find((x) => x.id === bid);
  if (!r) throw new ApiError(404, 'not_found', 'Teilnehmer nicht gefunden');
  return r;
}

const updateCombatant: Handler = async ([cid, kid, bid], body) => {
  const input = parse(updateCombatantSchema, body);
  const combat = await need<CombatDTO>(combatUrl(cid!, kid!));
  const r = combatantOf(combat, bid!);
  const { patch, log } = rule(() => planCombatantUpdate(r, input));
  const groupInit = input.applyToGroup && r.groupId && patch.initiative !== undefined;
  const combatants = combat.combatants.map((x) => {
    if (x.id === r.id) return { ...x, ...patch };
    if (groupInit && x.groupId === r.groupId) return { ...x, initiative: patch.initiative! };
    return x;
  });
  return saveCombat(cid!, { ...combat, combatants, log: log.length ? appendCombatLog(combat.log, combat.round, log) : combat.log });
};

const changeHp: Handler = async ([cid, kid, bid], body): Promise<CombatActionResult> => {
  const { kind, amount } = parse(hpChangeSchema, body);
  const combat = await need<CombatDTO>(combatUrl(cid!, kid!));
  const r = combatantOf(combat, bid!);
  const ruleset = await rulesetOf(cid!);
  const { patch, notices, concentrationDc } = rule(() => planHpChange(r, kind, amount, ruleset));
  const next = await saveCombat(cid!, {
    ...combat,
    combatants: combat.combatants.map((x) => (x.id === r.id ? { ...x, ...patch } : x)),
    log: appendCombatLog(combat.log, combat.round, notices),
  });
  return { combat: next, notices, concentrationDc };
};

const turn: Handler = async ([cid, kid], body): Promise<CombatActionResult> => {
  const { direction } = parse(turnSchema, body);
  const combat = await need<CombatDTO>(combatUrl(cid!, kid!));
  const res = rule(() =>
    planTurn(combat.combatants, { status: combat.status, round: combat.round, activeCombatantId: combat.activeCombatantId }, direction),
  );
  const next = await saveCombat(cid!, {
    ...combat,
    status: 'active',
    round: res.state.round,
    activeCombatantId: res.state.activeCombatantId,
    combatants: combat.combatants.map((x) => (res.conditionUpdates.has(x.id) ? { ...x, conditions: res.conditionUpdates.get(x.id)! } : x)),
    log: appendCombatLog(combat.log, res.state.round, res.log),
  });
  return { combat: next, notices: res.notices };
};

const ROUTES: Route[] = [
  { method: 'PUT', re: new RegExp(`${C}/notes/${ID}$`), run: updateNote },
  { method: 'PATCH', re: new RegExp(`${C}/notes/${ID}/frontmatter$`), run: patchNoteFrontmatter },
  { method: 'POST', re: new RegExp(`${C}/notes/${ID}/log$`), run: appendLog },
  { method: 'PATCH', re: new RegExp(`${C}/maps/${ID}$`), run: updateMap },
  { method: 'PATCH', re: new RegExp(`${C}/maps/${ID}/pins/${ID}$`), run: updatePin },
  { method: 'PATCH', re: new RegExp(`${C}/maps/${ID}/tokens/${ID}$`), run: updateToken },
  { method: 'PATCH', re: new RegExp(`${C}/combats/${ID}/combatants/${ID}$`), run: updateCombatant },
  { method: 'POST', re: new RegExp(`${C}/combats/${ID}/combatants/${ID}/hp$`), run: changeHp },
  { method: 'POST', re: new RegExp(`${C}/combats/${ID}/turn$`), run: turn },
];

/** Lokale Ausführung einer Änderung, falls sie offline möglich ist. */
export function offlineHandler(method: string, url: string): ((body: unknown) => Promise<unknown>) | null {
  for (const r of ROUTES) {
    if (r.method !== method) continue;
    const m = r.re.exec(url);
    if (m) return (body: unknown) => r.run(m.slice(1).map((x) => x.toLowerCase()), body);
  }
  return null;
}
